import type { Referrer } from '../types';
import { ContextId } from './enums';

// Dictionary that maps referrers to the respective context ID
export const referrersToContextIds = {
  // Observe
  'settings.integrations_google_optimize': ContextId.GoogleOptimize,
  'settings.integrations_optimizely': ContextId.Optimizely,
  'settings.integrations_slack': ContextId.ObserveSlack,
  'settings.integrations_teams': ContextId.ObserveMsTeams,
  'settings.integrations_webhooks': ContextId.ObserveWebhooks,
  'analytics_product.integrations_slack': ContextId.ObserveSlack,
  'analytics_product.integrations_zapier': ContextId.ObserveZapier,
  'analytics_product.integrations_teams': ContextId.ObserveMsTeams,
  'analytics_product.integrations_webhooks': ContextId.ObserveWebhooks,
  'site.settings_targeting': ContextId.PageTargeting,
  'site.console_tracking': ContextId.ErrorConsole,
  funnels_splash_screen_upgrade_link: ContextId.Funnels,
  trends_splash_screen_upgrade_link: ContextId.Trends,
  'recordings.heatmaps_error_widget.empty': ContextId.ErrorConsole,
  'recordings.error_console.empty': ContextId.ErrorConsole,
  'recordings.session_info.error_widget.empty': ContextId.ErrorConsole,
  'recordings.recommended_error_widget.empty': ContextId.ErrorConsole,

  // Ask
  'settings.integrations_zapier': ContextId.AskZapier,
  'feedback.appearance.accent_color': ContextId.SurveyCustomization,
  'feedback.appearance.widget_label': ContextId.SurveyCustomization,
  'feedback.branding.toggle': ContextId.RemoveHotjarBranding,
  'feedback.emotion_style_choices': ContextId.CustomReactionScales,
  'feedback.limit.reached': ContextId.IncomingFeedback,
  'feedback.targeting.attributes': ContextId.UserAttributes,
  'feedback.targeting.cap': ContextId.PercentageTargeting,
  'feedback.targeting.triggers': ContextId.AskEventsApi,
  'polls.branding.toggle': ContextId.RemoveHotjarBranding,
  'polls.go-unlimited': ContextId.Surveys,
  'polls.limit.reached': ContextId.Surveys,
  'polls.targeting.attributes': ContextId.UserAttributes,
  'polls.targeting.cap': ContextId.PercentageTargeting,
  'polls.targeting.triggers': ContextId.AskEventsApi,
  'survey.appearance.colors': ContextId.SurveyCustomization,
  'survey.emotion_style_choices': ContextId.CustomReactionScales,
  'surveys.performance': ContextId.SurveyPerformance,
  'survey.question.images': ContextId.SurveyQuestionImages,
  'user-attributes.upgrade': ContextId.UserAttributes,
  'feedback_product.integrations_slack': ContextId.AskSlack,
  'feedback_product.integrations_zapier': ContextId.AskZapier,
  'feedback_product.integrations_teams': ContextId.AskMsTeams,
  'feedback_product.integrations_webhooks': ContextId.AskWebhooks,
  'overview.upsell_banner.ask': ContextId.Surveys,
  'feedback_product.response_limit_50_percent': ContextId.AskResponseLimit,
  'feedback_product.response_limit_100_percent': ContextId.AskResponseLimit,
  public_api: ContextId.PublicAPI,

  // Observe filters
  'analytics_product.filters.google_optimize': ContextId.GoogleOptimize,
  'analytics_product.filters.optimizely': ContextId.Optimizely,
  'analytics_product.filters.incoming_feedback': ContextId.FilterIncomingFeedback,
  'analytics_product.filters.nps': ContextId.FilterNps,
  'analytics_product.filters.referrer_url': ContextId.FilterReferrerUrl,
  'analytics_product.filters.rage_click': ContextId.FilterRageClicks,
  'analytics_product.filters.user_attribute': ContextId.FilterUserAttributes,
  'analytics_product.filters.u_turn': ContextId.FilterUTurn,
  'analytics_product.filters.favorite': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.visited_url': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.landing_url': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.exit_url': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.new_returning_user': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.clicked': ContextId.FilterClicked,
  'analytics_product.filters.country': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.session_duration': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.page_count': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.tag': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.events': ContextId.ObserveEventsApi,
  'analytics_product.filters.user_id': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.team_comment': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.device': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.browser': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.os': ContextId.ObserveAdvancedFilters,
  'analytics_product.filters.menu_footer': ContextId.ObserveBusinessFilters,
  'analytics_product.filters.error': ContextId.FilterError,

  // Ask filters
  'feedback_product.filters.device': ContextId.AskAdvancedFilters,
  'feedback_product.filters.country': ContextId.AskAdvancedFilters,
  'feedback_product.filters.menu_footer': ContextId.AskAdvancedFilters,
  'survey.filters.country': ContextId.AskAdvancedFilters,
  'survey.filters.device': ContextId.AskAdvancedFilters,
  'survey.filters.user_attribute': ContextId.AskAdvancedFilters,
  'survey.filters.menu_footer': ContextId.AskAdvancedFilters,

  // Recordings segments (Plus)
  'recordings.segments.filters.browser': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.country': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.device': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.events': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.exit_url': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.favorite': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.landing_url': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.new_returning_user': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.os': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.page_count': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.session_duration': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.tag': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.team_comment': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.user_id': ContextId.FilterUserSegmentsPlus,
  'recordings.segments.filters.visited_url': ContextId.FilterUserSegmentsPlus,

  // Recordings segments (Business)
  'recordings.segments.filters.clicked': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.google_optimize': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.optimizely': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.incoming_feedback': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.nps': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.rage_click': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.referrer_url': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.u_turn': ContextId.FilterUserSegmentsBusiness,
  'recordings.segments.filters.user_attribute': ContextId.FilterUserSegmentsBusiness,

  // Recordings segments (Scale)
  'recordings.segments.filters.error': ContextId.FilterUserSegmentsScale,

  // Traffic
  'sampling-widget.tracking-coverage-message.sumup.available-plans': ContextId.Traffic,
  'sampling.widget_active-traffic-coverage-rate_upgrade-to-increase-limit': ContextId.Traffic,
  'sampling-widget.indicator-direct-upsell': ContextId.Traffic,
  'recordings.continuous-capture.increase-daily-limit-filtering-no-matches': ContextId.Traffic,
  'site.settings-session_capture_toggle-increase.daily.limit': ContextId.Traffic,
  'widget-empty-state': ContextId.Traffic,
  dashboard_top_banner_upgrade_link: ContextId.Traffic,
  funnels_top_banner_upgrade_link: ContextId.Traffic,
  recordings_guide_card: ContextId.Traffic,
  recordings_upsell_banner: ContextId.Traffic,
  trends_top_banner_upgrade_link: ContextId.Traffic,
  'heatmaps.traffic-indicator': ContextId.Traffic,
  'heatmaps.no-data': ContextId.Traffic,
  'campaign_traffic-coverage-modal': ContextId.Traffic,
  'overview.upsell_banner.observe': ContextId.Traffic,

  // Multi-product (or unknown)
  'navigation.top-nav.upgrade-link': ContextId.Default,
  'overview.upsell_banner.generic': ContextId.Default,
  'heatmaps.limit.reached': ContextId.Default,
  'heatmaps.go-unlimited': ContextId.Default,
  'heatmaps-go-unlimited': ContextId.Default,
  'heatmaps.create.downgraded': ContextId.Default,
  'heatmaps.list.downgraded': ContextId.Default,
  'heatmaps.snapshot-size-2000': ContextId.Default,
  'heatmaps.snapshot-size-10000': ContextId.Default,
  'heatmaps-sample-size': ContextId.Default,
  'Space Guide Card': ContextId.Default,
  'site-bar-link': ContextId.Default,
  'subscription-cost-summary-business': ContextId.Default,
} as const satisfies Record<Referrer, ContextId>;
